/**
 * THE STORY OF ONE TURN — the single place a turn's state is told.
 *
 * Status line (dot · label · elapsed) → the sentence under it → the phase row → the activity rows.
 * Everything comes from `presentTurn` (`laneV2/presentation.ts`), so no other block in the turn
 * may state a status: the answer is prose, the decision is a card, and this strip is the state.
 *
 * Calm by contract: the phase row appears only once a task exists; the activity rows show the
 * newest few; the clock appears after `ELAPSED_AFTER_MS` so a quick answer never shows a stopwatch.
 *
 * When it is over, the strip says WHY in one sentence (`ending`), then — for a failure — the concrete
 * facts (`details`: which components, which checks) and a way forward ("Ask what happened" sends a question that
 * names the failed request; "View details" opens the task). The task's path (`history`, the R3
 * transitions with the reader's own answers folded in) stays behind one disclosure, so a reopened
 * session still has its story without a wall of rows.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Check, ChevronRight, Circle, Minus, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { refSuffix } from "@/services/multi-agent/laneV2/conversation";
import { askWhatHappenedText, makeCancel, makeTaskControl } from "@/services/multi-agent/laneV2/chatActions";
import type { StoryRow, TurnPresentation } from "@/services/multi-agent/laneV2/presentation";
import type { UsageTotals } from "@/services/multi-agent/laneV2/types";
import { LaneV2TurnUsageChip } from "@/components/molecules/multi-agent/UsageMeter";
import { AnswerSkeleton } from "../transcript/TranscriptBlocks";

/** Only a wait long enough to make someone wonder earns a clock. */
export const ELAPSED_AFTER_MS = 3000;
/** Rows the strip shows inline; the rest are the task's history, in the workspace. */
const ROWS_SHOWN = 4;

export function formatElapsed(ms: number): string {
  const s = Math.max(0, Math.floor(ms / 1000));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ${s % 60}s`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ${m % 60}m`;
  return `${Math.floor(h / 24)}d ${h % 24}h`;
}

/** Ticks once a second while `since` is set; returns null until the threshold has passed. */
export function useElapsed(since: string | null, now: () => number = () => Date.now()): number | null {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (!since) return;
    const id = window.setInterval(() => setTick((t) => t + 1), 1000);
    return () => window.clearInterval(id);
  }, [since]);
  return useMemo(() => {
    if (!since) return null;
    const ms = now() - new Date(since).getTime();
    return ms >= ELAPSED_AFTER_MS ? ms : null;
    // `tick` is the clock; it is in the deps on purpose.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [since, tick, now]);
}

function clockTime(iso: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
}

/** A row of the task's history: a state it reached, or a question the reader settled. */
function HistoryRow({ row }: { row: StoryRow }) {
  const time = clockTime(row.at);
  return (
    <li className="flex items-baseline gap-2 text-[12px] text-muted-foreground" data-lane-v2-history-row={row.tone}>
      <span
        aria-hidden
        className={cn(
          "ma-dot",
          row.tone === "ok" && "ma-dot--ok",
          row.tone === "warn" && "ma-dot--warn",
          row.tone === "err" && "ma-dot--err",
        )}
        style={{ width: 6, height: 6, flex: "none", alignSelf: "center" }}
      />
      <span className={cn("min-w-0 flex-1", row.tone === "err" && "text-[var(--ma-state-err-text)]")}>{row.text}</span>
      {time && row.at && (
        <time dateTime={row.at} className="tabular-nums opacity-80">
          {time}
        </time>
      )}
    </li>
  );
}

const DOT: Record<TurnPresentation["kind"], string> = {
  working: "ma-dot--busy",
  waiting: "ma-dot--warn",
  completed: "ma-dot--ok",
  answered: "ma-dot--ok",
  failed: "ma-dot--err",
  stopped: "ma-dot--warn",
};

const LABEL_TONE: Record<TurnPresentation["kind"], string> = {
  working: "text-foreground",
  waiting: "text-[var(--ma-state-warn-text)]",
  completed: "text-[var(--ma-state-ok-text)]",
  answered: "text-[var(--ma-state-ok-text)]",
  failed: "text-[var(--ma-state-err-text)]",
  stopped: "text-[var(--ma-state-warn-text)]",
};

/** Filled circles: a tick when done, the live dot while active, a cross when failed; a quiet ring for what is still ahead. */
function PhaseMark({ state }: { state: NonNullable<TurnPresentation["phases"]>[number]["state"] }) {
  const base = "inline-flex h-4 w-4 flex-none items-center justify-center rounded-full";
  if (state === "done") {
    return (
      <span aria-hidden className={cn(base, "bg-foreground/70 text-background")} data-phase-mark="done">
        <Check className="h-2.5 w-2.5" strokeWidth={3} />
      </span>
    );
  }
  if (state === "failed") {
    return (
      <span aria-hidden className={cn(base, "bg-[var(--ma-state-err-text)] text-background")} data-phase-mark="failed">
        <X className="h-2.5 w-2.5" strokeWidth={3} />
      </span>
    );
  }
  if (state === "stopped") {
    return (
      <span aria-hidden className={cn(base, "border border-border text-muted-foreground")} data-phase-mark="stopped">
        <Minus className="h-2.5 w-2.5" strokeWidth={3} />
      </span>
    );
  }
  if (state === "active") {
    return (
      <span aria-hidden className={cn(base, "border border-border bg-muted")} data-phase-mark="active">
        <span className="ma-dot ma-dot--busy" style={{ width: 7, height: 7 }} />
      </span>
    );
  }
  if (state === "waiting") {
    return <span aria-hidden className={cn(base, "bg-[var(--ma-state-warn-text)]")} data-phase-mark="waiting"><span className="h-1.5 w-1.5 rounded-full bg-background" /></span>;
  }
  return <span aria-hidden className={cn(base, "border border-border")} data-phase-mark="todo" />;
}

function PhaseRow({ phases }: { phases: NonNullable<TurnPresentation["phases"]> }) {
  return (
    <ol className="flex flex-wrap items-center gap-x-3 gap-y-1" aria-label="Progress">
      {phases.map((p) => (
        <li
          key={p.key}
          data-phase={p.key}
          data-phase-state={p.state}
          className={cn(
            "inline-flex items-center gap-1 text-[12px]",
            p.state === "active" && "font-medium text-foreground",
            p.state === "waiting" && "font-medium text-[var(--ma-state-warn-text)]",
            p.state === "done" && "text-muted-foreground",
            p.state === "failed" && "text-[var(--ma-state-err-text)]",
            (p.state === "todo" || p.state === "stopped") && "text-muted-foreground opacity-60",
          )}
        >
          <PhaseMark state={p.state} />
          {p.label}
          <span className="sr-only">{`: ${p.state}`}</span>
        </li>
      ))}
    </ol>
  );
}

export interface LaneV2TurnStoryProps {
  story: TurnPresentation;
  /** `workingSince` for the live turn; null otherwise. */
  since: string | null;
  /** Open the workspace Task section on this story's task. */
  onOpenDetails?: () => void;
  /** "Try again" on a failed send/turn: the host re-sends the request (or, without a send path, puts it back in the composer). */
  onRetry?: () => void;
  /** "Ask what happened" on a failed task: the host sends the question (or, without a send path, fills the composer). */
  onAsk?: (question: string) => void;
  /**
   * Cancel the story's task (UX map C8): shown while the task is cancellable (`story.cancellable`, never at rest). Resolves to the
   * refusal copy when the server declined, else null. Omitted: no button.
   */
  onCancelTask?: () => Promise<string | null>;
  /**
   * "Pause after this component" / "Resume" (UX map B1, D3): offered only when R3 `task_controls` says so (`story.controls`) AND the host
   * wires the handler. Resolve to the typed status; throw on a refusal (its copy is `taskControlFailureCopy`).
   */
  onHoldTask?: () => Promise<{ status: string }>;
  onResumeTask?: () => Promise<{ status: string }>;
  /**
   * The task's outcome card is shown in this turn and owns the status line ("N components changed · Checked 14:02 · $0.41"
   * and its one "Details ›"): the strip keeps only what is live, a failure's facts, and the Cancel / Pause / Resume buttons.
   */
  folded?: boolean;
  /** The request this story is about (the user's message), so "Ask what happened" names it. */
  requestTitle?: string | null;
  /** R5 per-turn usage, shown muted at the end of the status line once settled. */
  usage?: UsageTotals | null;
  /** The reply as it is being written (`turn_text_delta`), for the live turn only; R5's reply replaces it. */
  draft?: string | null;
  /** The reply is streaming as normal prose in the turn above (a Q&A turn): no skeleton here, and no draft box. */
  proseStreaming?: boolean;
  className?: string;
}

/** The reply as it is being written: secondary, capped, and quiet for screen readers (no live region). */
function LiveDraftArea({ text }: { text: string }) {
  return (
    <details open className="group text-[12px]" data-lane-v2-draft="">
      <summary className="inline-flex cursor-pointer list-none items-center gap-1 font-medium text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
        <ChevronRight aria-hidden className="h-3.5 w-3.5 transition-transform group-open:rotate-90" />
        <span>Writing…</span>
        <span className="sr-only"> (draft reply, not final)</span>
      </summary>
      <p className="mt-1 max-h-28 overflow-y-auto whitespace-pre-wrap break-words border-l-2 border-border pl-3 text-muted-foreground">{text}</p>
    </details>
  );
}

export function LaneV2TurnStory({ story, since, onOpenDetails, onRetry, onAsk, onCancelTask, onHoldTask, onResumeTask, folded = false, requestTitle, usage, draft, proseStreaming = false, className }: LaneV2TurnStoryProps) {
  const elapsed = useElapsed(story.live ? since : null);
  const [cancelling, setCancelling] = useState(false);
  const [cancelMessage, setCancelMessage] = useState<string | null>(null);
  const cancelBusy = useRef(false);
  const cancel = onCancelTask ? makeCancel({ run: onCancelTask, busyRef: cancelBusy, setBusy: setCancelling, setMessage: setCancelMessage }) : null;
  const [controlBusy, setControlBusy] = useState(false);
  const [controlMessage, setControlMessage] = useState<string | null>(null);
  const controlRef = useRef(false);
  // A message about a control that is no longer on offer (a pending hold dropped when the task moved on) must not linger.
  useEffect(() => {
    setControlMessage(null);
  }, [story.controls.hold, story.controls.resume, story.controls.holdPending]);
  const hold = onHoldTask && story.controls.hold
    ? makeTaskControl({ kind: "hold", run: onHoldTask, busyRef: controlRef, setBusy: setControlBusy, setMessage: setControlMessage })
    : null;
  const resume = onResumeTask && story.controls.resume
    ? makeTaskControl({ kind: "resume", run: onResumeTask, busyRef: controlRef, setBusy: setControlBusy, setMessage: setControlMessage })
    : null;
  const rows = story.activity.slice(-ROWS_SHOWN);
  const showActivity = story.live && rows.length > 0;
  const draftText = story.live && draft && draft.trim() ? draft : null;
  // Nothing to narrate yet: the shimmer says "alive" until the first activity row (or draft) arrives.
  const showSkeleton = story.live && story.kind === "working" && rows.length === 0 && !draftText && !proseStreaming;
  const over = story.kind === "completed" || story.kind === "failed" || story.kind === "stopped";
  // One row is no path: a task that only ever "started" has nothing to disclose.
  const showHistory = over && story.history.length > 1;
  // Folded under the card: its header says what the strip would (done / needs a look / waiting); only a live or failed story keeps its line.
  const showStatusLine = !(folded && !story.live && (story.kind === "completed" || story.kind === "waiting"));
  const showOpener = !folded;
  const buttonClass =
    "inline-flex items-center rounded-md border border-border px-2.5 py-1 text-[13px] font-medium text-foreground hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";
  // The one primary action of a failure: the accent, so it reads as the way forward.
  const primaryClass =
    "inline-flex items-center rounded-md border border-primary bg-primary px-2.5 py-1 text-[13px] font-medium text-primary-foreground hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";
  return (
    <section
      aria-label="What Weave is doing"
      data-lane-v2-story={story.kind}
      className={cn("flex flex-col gap-2", className)}
    >
      {showStatusLine && (
      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5" role="status" aria-live="polite">
        <span aria-hidden className={cn("ma-dot", DOT[story.kind])} style={{ width: 7, height: 7, flex: "none", alignSelf: "center" }} />
        <span
          className={cn("text-[13px] font-semibold", story.live && story.kind === "working" ? "ma-weave-shimmer" : LABEL_TONE[story.kind])}
          data-lane-v2-status-label=""
        >
          {story.statusLabel}
        </span>
        {elapsed !== null && (
          <span className="text-[12px] tabular-nums text-muted-foreground" data-lane-v2-elapsed="">
            · {formatElapsed(elapsed)}
          </span>
        )}
        {usage && !story.live && (
          <span className="text-[12px] text-muted-foreground">
            · <LaneV2TurnUsageChip usage={usage} />
          </span>
        )}
        {(story.statusDescription || story.refCode) && (
          <span className="basis-full text-[13px] text-muted-foreground">
            {story.statusDescription}
            {story.statusDescription && story.refCode ? " " : ""}
            {story.refCode && (
              <span className="opacity-70" data-lane-v2-failure-ref="">
                {refSuffix(story.refCode)}
              </span>
            )}
          </span>
        )}
      </div>
      )}

      {story.phases && (!folded || story.live) && <PhaseRow phases={story.phases} />}

      {showActivity && (
        <ul className="flex flex-col gap-0.5 border-l-2 border-border pl-3" data-lane-v2-activity="">
          {rows.map((row, i) => {
            const newest = i === rows.length - 1;
            return (
              <li key={row.key} className="flex items-center gap-2 text-[12px] text-muted-foreground">
                {row.done === true ? (
                  <Check aria-hidden className={cn("h-3 w-3", row.tone === "err" ? "text-[var(--ma-state-err-text)]" : "text-[var(--ma-state-ok-text)]")} />
                ) : newest && story.live ? (
                  <span aria-hidden className="ma-dot ma-dot--busy" style={{ width: 6, height: 6 }} />
                ) : (
                  <Circle aria-hidden className="h-2.5 w-2.5" strokeWidth={2} />
                )}
                <span className={cn(newest && story.live && row.done !== true && "ma-weave-shimmer font-medium")}>{row.text}</span>
                {row.count > 1 && <span className="tabular-nums">×{row.count}</span>}
              </li>
            );
          })}
        </ul>
      )}

      {showSkeleton && <AnswerSkeleton announce={false} />}

      {draftText && <LiveDraftArea text={draftText} />}

      {story.details.length > 0 && (
        <ul className="flex flex-col gap-0.5 border-l-2 border-ma-err-line pl-3" data-lane-v2-failure-details="" aria-label="What went wrong">
          {story.details.map((text) => (
            <li key={text} className="flex items-start gap-2 text-[12px] text-foreground">
              <X aria-hidden className="mt-0.5 h-3 w-3 flex-none text-[var(--ma-state-err-text)]" />
              <span className="min-w-0 break-words">{text}</span>
            </li>
          ))}
        </ul>
      )}

      {showHistory && (
        <details className="group text-[12px]" data-lane-v2-history="">
          <summary className="inline-flex cursor-pointer list-none items-center gap-1 font-medium text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
            <ChevronRight aria-hidden className="h-3.5 w-3.5 transition-transform group-open:rotate-90" />
            {story.kind === "completed" ? "What happened" : "How it got here"}
            <span className="font-normal">· {story.history.length} {story.history.length === 1 ? "step" : "steps"}</span>
          </summary>
          <ol className="mt-1.5 flex flex-col gap-1 border-l-2 border-border pl-3">
            {story.history.map((row) => (
              <HistoryRow key={row.key} row={row} />
            ))}
          </ol>
        </details>
      )}

      {(story.failure || (story.taskId && (showOpener || hold || resume)) || (story.cancellable && onCancelTask)) && (
        <div className="flex flex-wrap items-center gap-2">
          {story.failure?.retryable && onRetry && (
            <button type="button" onClick={onRetry} className={primaryClass} data-lane-v2-retry="">
              Try again
            </button>
          )}
          {story.kind === "failed" && story.taskId && onAsk && (
            <button type="button" onClick={() => onAsk(askWhatHappenedText(requestTitle))} className={buttonClass} data-lane-v2-ask-what-happened="">
              Ask what happened
            </button>
          )}
          {story.cancellable && story.taskId && onCancelTask && (
            <button type="button" onClick={() => void cancel?.()} disabled={cancelling} className={cn(buttonClass, "text-muted-foreground disabled:opacity-50")} data-lane-v2-cancel-task={story.taskId}>
              {cancelling ? "Cancelling..." : "Cancel task"}
            </button>
          )}
          {resume && (
            <button type="button" onClick={() => void resume()} disabled={controlBusy} className={cn(primaryClass, "disabled:opacity-50")} data-lane-v2-resume-task={story.taskId}>
              {controlBusy ? "Resuming..." : "Resume"}
            </button>
          )}
          {hold && (
            <button type="button" onClick={() => void hold()} disabled={controlBusy} className={cn(buttonClass, "text-muted-foreground disabled:opacity-50")} data-lane-v2-hold-task={story.taskId}>
              {controlBusy ? "Pausing..." : story.controls.building ? "Pause after this component" : "Pause"}
            </button>
          )}
          {showOpener && story.taskId && onOpenDetails && (
            <button
              type="button"
              onClick={onOpenDetails}
              data-lane-v2-open-task={story.taskId}
              className="inline-flex items-center gap-0.5 rounded-md px-1.5 py-1 text-[13px] font-medium text-muted-foreground hover:bg-muted/50 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              {story.kind === "completed" ? "Review changes" : "View details"}
              <ChevronRight aria-hidden className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      )}
      {controlMessage && (
        <p role="status" className="text-[13px] text-muted-foreground" data-lane-v2-control-message="">
          {controlMessage}
        </p>
      )}
      {cancelMessage && (
        <p role="status" className="text-[13px] text-muted-foreground" data-lane-v2-cancel-message="">
          {cancelMessage}
        </p>
      )}
    </section>
  );
}
